import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { ArrowUpRight16Filled } from "@fluentui/react-icons";
import axios from "axios";
import "./ProjectEmails.css";

function ProjectEmails({ projectData }) {
  const { instance, accounts } = useMsal();
  const [emails, setEmails] = useState([]);
  const [uniqueEmails, setUniqueEmails] = useState(new Set());
  const [filteredNettlescsTeam, setFilteredNettlescsTeam] = useState(new Set());
  const [otherSectionsEmails, setOtherSectionsEmails] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (projectData?.opportunityUUID) {
      fetchContacts(projectData.opportunityUUID);
    }
  }, [projectData]);

  const fetchContacts = async (opportunityUUID) => {
    try {
      const response = await axios.get(
        `https://projects-dev.nettlescs.com:5000/api/contacts/opportunity/${opportunityUUID}`
      );

      const contacts = Array.isArray(response.data.data)
        ? response.data.data
        : [];

      if (contacts.length === 0) {
        console.warn("No contacts found.");
        setUniqueEmails(new Set());
        return;
      }

      const emailsSet = new Set();
      const nettlescsTeam = new Set();

      // Collecting emails for Nettles Project Team
      contacts.forEach((contact) => {
        if (contact.Group === "Nettles Project Team") {
          if (contact.Email) nettlescsTeam.add(contact.Email.toLowerCase());
          if (contact.Email_Alternate__c)
            nettlescsTeam.add(contact.Email_Alternate__c.toLowerCase());
          if (contact.Personal_Email__c)
            nettlescsTeam.add(contact.Personal_Email__c.toLowerCase());
        }
      });

      // Convert nettlescsTeam to an array and filter only those containing "nettlescs.com"
      const filteredNettlescsTeamSet = new Set(
        [...nettlescsTeam].filter((email) => email.includes("nettlescs.com"))
      );

      // Collect all email addresses
      contacts.forEach((contact) => {
        if (contact.Email) emailsSet.add(contact.Email.toLowerCase());
        if (contact.Email_Alternate__c)
          emailsSet.add(contact.Email_Alternate__c.toLowerCase());
        if (contact.Personal_Email__c)
          emailsSet.add(contact.Personal_Email__c.toLowerCase());
      });

      // Compute other section emails (all emails except those in filteredNettlescsTeam)
      const otherSectionsEmailsSet = new Set(
        [...emailsSet].filter((email) => !filteredNettlescsTeamSet.has(email))
      );

      setUniqueEmails(emailsSet);
      setFilteredNettlescsTeam(filteredNettlescsTeamSet);
      setOtherSectionsEmails(otherSectionsEmailsSet);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setError("Failed to fetch contacts.");
    }
  };

  useEffect(() => {
    if (accounts.length > 0) {
      setLoading(true);
      instance
        .acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        })
        .then((response) => {
          fetchEmails(response.accessToken);
        })
        .catch((err) => {
          console.error("Error acquiring token for emails:", err);
          setError("Failed to fetch emails. Please try again.");
          setLoading(false);
        });
    }
  }, [accounts, instance, uniqueEmails]);

  const fetchEmails = async (accessToken) => {
    try {
      const inboxEndpoint = `https://graph.microsoft.com/v1.0/me/messages`;
      const sentItemsEndpoint = `https://graph.microsoft.com/v1.0/me/mailFolders/SentItems/messages`;

      const [inboxResponse, sentItemsResponse] = await Promise.all([
        fetch(inboxEndpoint, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(sentItemsEndpoint, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);

      if (!inboxResponse.ok || !sentItemsResponse.ok) {
        console.error("Failed to fetch emails:", {
          inboxError: await inboxResponse.json(),
          sentItemsError: await sentItemsResponse.json(),
        });
        setError("Failed to fetch emails. Please try again.");
        setLoading(false);
        return;
      }

      const inboxData = await inboxResponse.json();
      const sentItemsData = await sentItemsResponse.json();

      const inboxEmails = (inboxData.value || []).map((email) => ({
        id: email.id,
        sender: email.from?.emailAddress?.name || "Unknown Sender",
        senderEmail: email.from?.emailAddress?.address || "",
        time: email.receivedDateTime || "Unknown Time",
        subject: email.subject || "No Subject",
        folder: "Inbox",
        isSelected: false,
        ccRecipients:
          email.ccRecipients?.map((cc) => cc.emailAddress.address) || [],
      }));

      const sentEmails = (sentItemsData.value || []).map((email) => ({
        id: email.id,
        sender: "You",
        recipient:
          email.toRecipients?.[0]?.emailAddress?.name || "Unknown Recipient",
        recipientEmail: email.toRecipients?.[0]?.emailAddress?.address || "",
        time: email.sentDateTime || "Unknown Time",
        subject: email.subject || "No Subject",
        folder: "Sent",
        isSelected: false,
        ccRecipients:
          email.ccRecipients?.map((cc) => cc.emailAddress.address) || [],
      }));

      const combinedEmails = [...inboxEmails, ...sentEmails];

      const dedupedEmails = Array.from(
        new Map(combinedEmails.map((email) => [email.id, email])).values()
      );

      const lowerCaseUniqueEmails = new Set(
        Array.from(uniqueEmails).map((email) => email.toLowerCase())
      );

      const finalFilteredEmails = dedupedEmails.filter((email) => {
        const sender = (email.senderEmail || "").toLowerCase();
        const recipient = (email.recipientEmail || "").toLowerCase();
        const ccRecipients = email.ccRecipients || [];
      
        // Convert to lowercase for comparison
        console.log(lowerCaseUniqueEmails);
        const lowerCaseCCRecipients = ccRecipients.map(cc => cc.toLowerCase());
      
        // Condition 1: Exclude emails if sender or recipient is in filteredNettlescsTeam
        const isExcluded = filteredNettlescsTeam.has(sender) || filteredNettlescsTeam.has(recipient);
      
        // Condition 2: If excluded, include back if any ccRecipient is in otherSectionsEmails
        if (isExcluded) {
          return lowerCaseCCRecipients.some(cc => otherSectionsEmails.has(cc));
        }
      
        // Otherwise, only include if sender or recipient is in lowerCaseUniqueEmails
        return lowerCaseUniqueEmails.has(sender) || lowerCaseUniqueEmails.has(recipient);
      });
      

      setEmails(finalFilteredEmails.slice(0, 5));
      setLoading(false);
    } catch (err) {
      console.error("Error fetching emails:", err);
      setError("Failed to fetch emails. Please try again.");
      setLoading(false);
    }
  };
  const handleCheckboxChange = (id) => {
    setEmails((prevEmails) =>
      prevEmails.map((email) =>
        email.id === id ? { ...email, isSelected: !email.isSelected } : email
      )
    );
  };

  return (
    <section className="project-emails">
      <div className="emails-header">
        <h2 className="emails-title">Project Emails</h2>
        <button
          className="view-all-btn"
          onClick={() =>
            window.open("https://outlook.office.com/mail/", "_blank")
          }
        >
          View All
          <ArrowUpRight16Filled />
        </button>
      </div>
      <div className="email-list">
        {emails.length > 0 ? (
          emails.map((email) => (
            <div key={email.id} className="email-item">
              <div className="email-header">
                <div className="sender-info">
                  <input
                    type="checkbox"
                    checked={email.isSelected}
                    onChange={() => handleCheckboxChange(email.id)}
                    className="email-checkbox"
                  />
                  <span className="sender-name">{email.sender}</span>
                </div>
                <span className="email-time">
                  {new Date(email.time).toLocaleDateString([], {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}{" "}
                  {new Date(email.time).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <br />
                <span className="email-folder">{email.folder}</span>
              </div>
              <p className="email-subject">{email.subject}</p>
            </div>
          ))
        ) : (
          <div className="no-emails-found">
            <p>No emails for the selected project.</p>
          </div>
        )}
      </div>
    </section>
  );
}

export default ProjectEmails;